import { TimeSeriesTrendsResult } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { TimeSeriesTrendsRequest } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { getDimensionByColumnHeaderValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { GROUP_BY_DEFAULT_ITEM_KEY } from 'libs/end-to-end/src/filters-v2/filter-utils';

export const getTimeSeriesTrendsExplanationAttributes = ({
  request,
  myObservation,
}: {
  request: TimeSeriesTrendsRequest;
  myObservation: MyObservation | null;
}): {
  metricDisplay: string;
  dimensionDisplay: string;
  isMetricBoolean: boolean;
} => {
  const metricName = request.inputs.extras.metric;
  const breakdownOption = request.inputs.extras.breakdownOption;

  let dimensionName = breakdownOption ?? '';
  if (breakdownOption === GROUP_BY_DEFAULT_ITEM_KEY || !breakdownOption) {
    dimensionName = 'overall average';
  }

  let metricDisplay = metricName + 's';
  let isMetricBoolean = false;

  if (myObservation) {
    for (const focusAreaDefinition of myObservation.focusAreas) {
      const metricDefinition = focusAreaDefinition.metrics.find(
        (m) => m.columnHeaderValue === metricName,
      );
      if (metricDefinition) {
        metricDisplay = metricDefinition.displayName;
        isMetricBoolean = metricDefinition.isMetricBoolean;
        break;
      }
    }
  }

  const dimensionDefinition = getDimensionByColumnHeaderValueFromObservation(
    myObservation,
    dimensionName,
  );

  const dimensionDisplay = dimensionDefinition?.displayName ?? dimensionName;

  return {
    metricDisplay: metricDisplay.toLowerCase(),
    dimensionDisplay: dimensionDisplay.toLowerCase(),
    isMetricBoolean,
  };
};

export const constructTimeSeriesTrendsExplanationCopy = ({
  request,
  myObservation,
}: {
  request: TimeSeriesTrendsRequest;
  myObservation: MyObservation | null;
  data?: TimeSeriesTrendsResult;
}): string => {
  const { metricDisplay, dimensionDisplay, isMetricBoolean } =
    getTimeSeriesTrendsExplanationAttributes({
      request,
      myObservation,
    });

  const timeGranularityMap: Record<string, string> = {
    day: 'day',
    week: 'week',
    month: 'month',
    quarter: 'quarter',
    year: 'year',
  } as const;

  const viewBy = request.inputs.extras.timeBoiOption as string;
  const granularityLabel = timeGranularityMap[viewBy] ?? 'period';

  const startYear = parseInt(request.inputs.extras.startDate.slice(0, 4), 10);
  const endYear = parseInt(request.inputs.extras.endDate.slice(0, 4), 10);

  const prefix = `Viewing by ${granularityLabel},`;

  if (isMetricBoolean) {
    return `${prefix} the chart shows ${metricDisplay} rates by ${dimensionDisplay} for the years ${startYear} to ${endYear}.`;
  }

  return `${prefix} the chart shows average ${metricDisplay} by ${dimensionDisplay} for the years ${startYear} to ${endYear}.`;
};
